import { AuthUser } from '../core/state/auth-store';
import { api } from '../core/network/api';
import { mountShell } from './shell';
import { initOfferBanner } from './offer-banner';
import { startMarchPolling } from './marches';
import { startBoostPolling } from './boosts';
import { initAudio } from './audio';
import { GameTab } from './tabs';
import { colonyTab } from '../screens/colony/colony-screen';
import { fetchColony } from '../screens/colony/colony-api';
import { renderFoundColonyScreen } from '../screens/colony/found-colony-screen';
import { worldMapTab } from '../screens/worldmap/worldmap-screen';
import { markHomeNoticePending } from './home-notice';
import { battleTab } from '../screens/battle/battle-screen';
import { profileTab } from '../screens/profile/profile-screen';
import { leaderboardTab } from '../screens/leaderboard/leaderboard-screen';
import { questsTab } from '../screens/quests/quests-screen';
import { shopTab } from '../screens/shop/shop-screen';
import { settingsTab } from '../screens/settings/settings-screen';
import { initNotifications } from '../screens/notifications/notifications';
import { initChatWidget } from './chat-widget';

/**
 * Engine bootstrap — assembles the game's tabs and mounts the app shell.
 * Each tab is a self-contained screen module (see engine/tabs.ts for the
 * contract); this file is the only place that wires them together, same
 * role as backend/src/game/game.module.ts on the server side.
 *
 * worldMapTab must stay tabs[0]: mountShell() calls switchTab(tabs[0].id) on
 * mount, so whichever tab is first becomes the post-login landing tab — the
 * Global map, centered on the player's own colony (see
 * worldmap-screen.ts's focusOnHomeColony call in load()).
 * Everything else is ordered core-loop-first, meta-last: colonyTab and
 * battleTab are the other primary gameplay loops; profileTab/leaderboardTab/
 * questsTab/shopTab are secondary/progression screens (leaderboardTab sits
 * right after profileTab — standing is progression-adjacent to the account
 * card, and the profile screen's "Full rankings" button jumps straight here);
 * settingsTab is appended last (gear-icon-last is the conventional slot in a
 * mobile tab bar).
 */
const GAME_TABS: GameTab[] = [
  worldMapTab,
  colonyTab,
  battleTab,
  profileTab,
  leaderboardTab,
  questsTab,
  shopTab,
  settingsTab,
];

let currentTeardown: (() => void) | null = null;
let stopNotifications: (() => void) | null = null;
let stopChatWidget: (() => void) | null = null;
let lastBrandTitle: string | null = null;

/**
 * The <title> tag is server-injected from `meta.title`/`BRAND_NAME` and never
 * touched by frontend JS otherwise (see frontend/server.js + meta-inject.js).
 * Once a colony is known, append its name so the browser tab identifies which
 * colony this session is — `GET /api/meta` is public and already resolves the
 * same brand name the server injected, so this never hardcodes the game's
 * name. A failed fetch just leaves whatever title is already on the page.
 */
async function setGameTitle(colonyName: string): Promise<void> {
  try {
    const meta = await api.get<{ title: string }>('/meta');
    lastBrandTitle = meta.title;
    document.title = `${meta.title} - ${colonyName}`;
  } catch {
    // meta unreachable — leave the existing title alone.
  }
}

/**
 * Mounts the normal tab shell — notifications poller, chat widget, and the
 * tab bar itself. Pulled out of mountGame() so both the "already has a
 * colony" path and the "just founded one" callback from the gate screen
 * below can reach it without duplicating setup.
 */
function mountNormalShell(root: HTMLElement, user: AuthUser, onLogout: () => void): () => void {
  stopNotifications = initNotifications();
  stopChatWidget = initChatWidget();
  // Arms background music on the first gesture; browsers refuse to start audio
  // before one, so this is the earliest honest moment.
  const stopAudio = initAudio();
  const shellTeardown = mountShell(root, user, GAME_TABS, onLogout);
  // Offer banner (once an hour) and the running-boost store the HUD icons read.
  const stopBanner = initOfferBanner();
  const stopBoosts = startBoostPolling();
  // The marches on the road, shown beside the boosts on the map and colony HUDs.
  const stopMarches = startMarchPolling();
  return () => {
    stopBanner();
    stopBoosts();
    stopMarches();
    shellTeardown();
    stopAudio();
    stopNotifications?.();
    stopNotifications = null;
    stopChatWidget?.();
    stopChatWidget = null;
  };
}

/**
 * Every tab degrades gracefully around "no colony yet" (worldmap stays
 * browsable, colony/profile/etc. show a plain empty state) but none of them
 * can actually CREATE one — there is no other path to `POST /me/colony` at
 * all. So before mounting the real tab shell, probe for a colony; a fresh
 * account (404) sees a one-time "found your colony" gate instead, and only
 * proceeds into the normal game once that succeeds.
 *
 * Only an explicit successful fetch counts as "has a colony" — any failure at
 * all (404, a network blip, a 500, ...) sends the account to the founding
 * gate instead of assuming success. Treating a non-404 error as "has a
 * colony" previously let an account with a genuinely orphaned colony row (one
 * that exists but was never actually placed on the map — see
 * ColonyService's boot-time reconciliation for the root cause) sail straight
 * into the normal shell with nothing to ever re-trigger the gate.
 */
export function mountGame(root: HTMLElement, user: AuthUser, onLogout: () => void): () => void {
  let disposed = false;
  let innerTeardown: (() => void) | null = null;

  (async () => {
    let colonyName: string | null = null;
    try {
      colonyName = (await fetchColony()).name;
    } catch {
      // Any failure at all means this account is not confirmed to have a
      // colony — see the doc comment above.
    }
    if (disposed) return;

    if (colonyName !== null) {
      void setGameTitle(colonyName);
      innerTeardown = mountNormalShell(root, user, onLogout);
      return;
    }
    innerTeardown = renderFoundColonyScreen(root, (foundedName, colonyId) => {
      void setGameTitle(foundedName);
      // The Map tab (the landing tab) centres on the new colony and shows the
      // one-time "first home colony" callout next to it.
      markHomeNoticePending(colonyId);
      innerTeardown?.();
      innerTeardown = mountNormalShell(root, user, onLogout);
    });
  })();

  currentTeardown = () => {
    disposed = true;
    innerTeardown?.();
    innerTeardown = null;
  };
  return currentTeardown;
}

export function unmountGame(): void {
  currentTeardown?.();
  currentTeardown = null;
  if (lastBrandTitle) document.title = lastBrandTitle;
}
